import { useEffect, useRef } from "react";

/**
 * Adds an "in" class to the element when it scrolls into view.
 * Pair with `.reveal` CSS class.
 */
export function useReveal(options = {}) {
  const ref = useRef(null);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("in");
            obs.unobserve(e.target);
          }
        });
      },
      { threshold: options.threshold ?? 0.15, rootMargin: options.rootMargin ?? "0px 0px -60px 0px" },
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, [options.threshold, options.rootMargin]);
  return ref;
}

/** Auto-applies reveal to all .reveal descendants under a root.
 *  Pass a `deps` array to re-observe new dynamically-rendered elements
 *  (e.g. when a filtered list changes). */
export function useRevealAll(rootRef, deps = []) {
  useEffect(() => {
    const root = rootRef.current || document;
    const targets = root.querySelectorAll(".reveal:not(.in)");
    if (!targets.length) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("in");
            obs.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -50px 0px" },
    );
    targets.forEach((t) => obs.observe(t));
    return () => obs.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rootRef, ...deps]);
}
